import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart2, Download, Calendar, TrendingUp, TrendingDown,
  FileText, Users, Briefcase, PenTool, Filter, RefreshCw,
  Loader2, ChevronDown, ArrowUpRight, ArrowDownRight,
  Clock, Target, Activity, Zap
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  RadialBarChart, RadialBar
} from 'recharts';

type TimeRange = 'week' | 'month' | 'quarter' | 'year' | 'all';
type ReportType = 'overview' | 'documents' | 'clients' | 'signatures' | 'productivity';

interface ReportData {
  documents: {
    total: number;
    thisMonth: number;
    lastMonth: number;
    byTemplate: Array<{ name: string; count: number; color: string }>;
    byMonth: Array<{ month: string; count: number; avgTime: number }>;
    byStatus: Array<{ status: string; count: number; color: string }>;
    avgGenerationTime: number;
  };
  clients: {
    total: number;
    newThisMonth: number;
    byType: Array<{ name: string; value: number; color: string }>;
    topClients: Array<{ name: string; documentCount: number; revenue: number }>;
    growth: Array<{ month: string; clients: number; cumulative: number }>;
  };
  matters: {
    total: number;
    active: number;
    completed: number;
    byType: Array<{ type: string; count: number; color: string }>;
    avgDaysToClose: number;
    timeline: Array<{ month: string; opened: number; closed: number }>;
  };
  signatures: {
    sent: number;
    completed: number;
    pending: number;
    declined: number;
    avgTimeToSign: number;
    completionRate: number;
    byWeek: Array<{ week: string; sent: number; completed: number }>;
  };
  productivity: {
    documentsPerDay: Array<{ date: string; count: number }>;
    timeByTask: Array<{ task: string; hours: number; color: string }>;
    efficiency: number;
    goalsHit: number;
    totalGoals: number;
  };
}

const COLORS = {
  primary: '#fbbf24',
  secondary: '#60a5fa',
  success: '#34d399',
  warning: '#f59e0b',
  danger: '#f87171',
  purple: '#a78bfa',
  pink: '#f472b6',
  cyan: '#22d3ee',
};

const CHART_COLORS = [
  COLORS.primary,
  COLORS.secondary,
  COLORS.success,
  COLORS.purple,
  COLORS.pink,
  COLORS.cyan,
];

export function Reports() {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>('month');
  const [reportType, setReportType] = useState<ReportType>('overview');
  const [data, setData] = useState<ReportData | null>(null);

  useEffect(() => {
    loadReportData();
  }, [timeRange]);

  const loadReportData = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 800));

    // Enhanced simulated data
    setData({
      documents: {
        total: 1847,
        thisMonth: 189,
        lastMonth: 156,
        byTemplate: [
          { name: 'Warranty Deed', count: 423, color: COLORS.primary },
          { name: 'LLC Operating Agreement', count: 312, color: COLORS.secondary },
          { name: 'Simple Will', count: 287, color: COLORS.success },
          { name: 'Quitclaim Deed', count: 245, color: COLORS.purple },
          { name: 'Articles of Org', count: 198, color: COLORS.pink },
          { name: 'Other', count: 382, color: COLORS.cyan },
        ],
        byMonth: [
          { month: 'Jul', count: 145, avgTime: 2.1 },
          { month: 'Aug', count: 162, avgTime: 2.0 },
          { month: 'Sep', count: 178, avgTime: 1.9 },
          { month: 'Oct', count: 156, avgTime: 2.2 },
          { month: 'Nov', count: 189, avgTime: 1.8 },
          { month: 'Dec', count: 156, avgTime: 2.3 },
        ],
        byStatus: [
          { status: 'Draft', count: 45, color: COLORS.warning },
          { status: 'Review', count: 28, color: COLORS.secondary },
          { status: 'Approved', count: 67, color: COLORS.success },
          { status: 'Signed', count: 89, color: COLORS.primary },
          { status: 'Complete', count: 456, color: COLORS.purple },
        ],
        avgGenerationTime: 2.3,
      },
      clients: {
        total: 156,
        newThisMonth: 12,
        byType: [
          { name: 'Individual', value: 98, color: COLORS.primary },
          { name: 'LLC', value: 32, color: COLORS.secondary },
          { name: 'Trust', value: 18, color: COLORS.success },
          { name: 'Corporation', value: 8, color: COLORS.purple },
        ],
        topClients: [
          { name: 'ABC Holdings LLC', documentCount: 45, revenue: 12500 },
          { name: 'Johnson Family Trust', documentCount: 38, revenue: 9800 },
          { name: 'Smith Properties', documentCount: 32, revenue: 8400 },
          { name: 'Miller Enterprises', documentCount: 28, revenue: 7200 },
          { name: 'Davis & Associates', documentCount: 24, revenue: 6100 },
        ],
        growth: [
          { month: 'Jul', clients: 8, cumulative: 128 },
          { month: 'Aug', clients: 11, cumulative: 139 },
          { month: 'Sep', clients: 6, cumulative: 145 },
          { month: 'Oct', clients: 9, cumulative: 154 },
          { month: 'Nov', clients: 14, cumulative: 168 },
          { month: 'Dec', clients: 12, cumulative: 180 },
        ],
      },
      matters: {
        total: 234,
        active: 67,
        completed: 167,
        byType: [
          { type: 'Real Estate Purchase', count: 45, color: COLORS.primary },
          { type: 'Estate Planning', count: 38, color: COLORS.secondary },
          { type: 'Business Formation', count: 32, color: COLORS.success },
          { type: 'Real Estate Sale', count: 28, color: COLORS.purple },
          { type: 'Trust Admin', count: 24, color: COLORS.pink },
        ],
        avgDaysToClose: 34,
        timeline: [
          { month: 'Jul', opened: 18, closed: 15 },
          { month: 'Aug', opened: 22, closed: 19 },
          { month: 'Sep', opened: 16, closed: 21 },
          { month: 'Oct', opened: 25, closed: 18 },
          { month: 'Nov', opened: 20, closed: 24 },
          { month: 'Dec', opened: 14, closed: 16 },
        ],
      },
      signatures: {
        sent: 423,
        completed: 387,
        pending: 36,
        declined: 12,
        avgTimeToSign: 2.4,
        completionRate: 91.5,
        byWeek: [
          { week: 'Week 1', sent: 28, completed: 25 },
          { week: 'Week 2', sent: 32, completed: 30 },
          { week: 'Week 3', sent: 24, completed: 22 },
          { week: 'Week 4', sent: 35, completed: 32 },
        ],
      },
      productivity: {
        documentsPerDay: [
          { date: 'Mon', count: 12 },
          { date: 'Tue', count: 18 },
          { date: 'Wed', count: 15 },
          { date: 'Thu', count: 22 },
          { date: 'Fri', count: 14 },
        ],
        timeByTask: [
          { task: 'Document Creation', hours: 24, color: COLORS.primary },
          { task: 'Client Communication', hours: 18, color: COLORS.secondary },
          { task: 'Review & Approval', hours: 12, color: COLORS.success },
          { task: 'Research', hours: 8, color: COLORS.purple },
          { task: 'Admin', hours: 6, color: COLORS.pink },
        ],
        efficiency: 87,
        goalsHit: 8,
        totalGoals: 10,
      },
    });
    setLoading(false);
  };

  const exportReport = () => {
    alert('Export functionality - would generate CSV/PDF report');
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-primary-800 border border-primary-600 rounded-lg p-3 shadow-lg">
          <p className="text-white font-medium">{label}</p>
          {payload.map((entry: any, idx: number) => (
            <p key={idx} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-accent-400 animate-spin" />
      </div>
    );
  }

  const documentGrowth = ((data.documents.thisMonth - data.documents.lastMonth) / data.documents.lastMonth * 100).toFixed(1);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <BarChart2 className="w-7 h-7 text-accent-400" />
            Reports & Analytics
          </h1>
          <p className="text-primary-400 mt-1">Track your practice performance</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as TimeRange)}
            className="px-4 py-2 bg-primary-800 border border-primary-600 text-white rounded-lg"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
            <option value="all">All Time</option>
          </select>
          
          <button
            onClick={exportReport}
            className="flex items-center gap-2 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-600"
          >
            <Download className="w-5 h-5" />
            Export
          </button>
          
          <button
            onClick={loadReportData}
            className="p-2 bg-primary-700 text-white rounded-lg hover:bg-primary-600"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="flex gap-2 bg-primary-800/50 p-1 rounded-lg w-fit">
        {(['overview', 'documents', 'clients', 'signatures', 'productivity'] as ReportType[]).map((type) => (
          <button
            key={type}
            onClick={() => setReportType(type)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
              reportType === type
                ? 'bg-accent-500 text-white'
                : 'text-primary-400 hover:text-white'
            }`}
          >
            {type}
          </button>
        ))}
      </div>

      {/* Overview Report */}
      {reportType === 'overview' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              label="Documents Generated"
              value={data.documents.total}
              change={parseFloat(documentGrowth)}
              icon={<FileText className="w-6 h-6" />}
              color="primary"
            />
            <MetricCard
              label="Active Clients"
              value={data.clients.total}
              change={8.3}
              icon={<Users className="w-6 h-6" />}
              color="success"
            />
            <MetricCard
              label="Active Matters"
              value={data.matters.active}
              change={-3.2}
              icon={<Briefcase className="w-6 h-6" />}
              color="secondary"
            />
            <MetricCard
              label="Signature Rate"
              value={`${data.signatures.completionRate}%`}
              change={2.3}
              icon={<PenTool className="w-6 h-6" />}
              color="purple"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Document Generation Trend - Area Chart */}
            <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
              <h3 className="font-semibold text-white mb-4">Document Generation Trend</h3>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={data.documents.byMonth}>
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={COLORS.primary}
                    fillOpacity={1}
                    fill="url(#colorCount)"
                    name="Documents"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Top Templates - Pie Chart */}
            <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
              <h3 className="font-semibold text-white mb-4">Documents by Template</h3>
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie
                      data={data.documents.byTemplate}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="count"
                    >
                      {data.documents.byTemplate.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {data.documents.byTemplate.slice(0, 5).map((template, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: template.color }} />
                      <span className="text-sm text-primary-300 flex-1 truncate">{template.name}</span>
                      <span className="text-sm text-white">{template.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Second Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Matter Status */}
            <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
              <h3 className="font-semibold text-white mb-4">Matter Flow</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.matters.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="opened" fill={COLORS.secondary} name="Opened" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="closed" fill={COLORS.success} name="Closed" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Client Types */}
            <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
              <h3 className="font-semibold text-white mb-4">Client Distribution</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={data.clients.byType}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {data.clients.byType.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Signature Stats */}
            <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
              <h3 className="font-semibold text-white mb-4">Signature Completion</h3>
              <div className="flex items-center justify-center">
                <ResponsiveContainer width={150} height={150}>
                  <RadialBarChart
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    barSize={12}
                    data={[{ value: data.signatures.completionRate, fill: COLORS.success }]}
                    startAngle={90}
                    endAngle={-270}
                  >
                    <RadialBar
                      background={{ fill: '#374151' }}
                      dataKey="value"
                      cornerRadius={6}
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
              </div>
              <div className="text-center -mt-4">
                <p className="text-3xl font-bold text-white">{data.signatures.completionRate}%</p>
                <p className="text-sm text-primary-400">Completion Rate</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documents Report */}
      {reportType === 'documents' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard label="Total Documents" value={data.documents.total.toLocaleString()} />
            <StatCard label="This Month" value={data.documents.thisMonth} trend={parseFloat(documentGrowth)} />
            <StatCard label="Avg. Generation Time" value={`${data.documents.avgGenerationTime}s`} />
            <StatCard label="Templates Used" value={data.documents.byTemplate.length} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Generation Over Time */}
            <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
              <h3 className="font-semibold text-white mb-4">Generation Over Time</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.documents.byMonth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9ca3af" />
                  <YAxis yAxisId="left" stroke="#9ca3af" />
                  <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="count" stroke={COLORS.primary} name="Documents" strokeWidth={2} dot={{ fill: COLORS.primary }} />
                  <Line yAxisId="right" type="monotone" dataKey="avgTime" stroke={COLORS.secondary} name="Avg Time (s)" strokeWidth={2} dot={{ fill: COLORS.secondary }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Document Status */}
            <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
              <h3 className="font-semibold text-white mb-4">Documents by Status</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.documents.byStatus} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#9ca3af" />
                  <YAxis dataKey="status" type="category" stroke="#9ca3af" width={80} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {data.documents.byStatus.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Clients Report */}
      {reportType === 'clients' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard label="Total Clients" value={data.clients.total} />
            <StatCard label="New This Month" value={`+${data.clients.newThisMonth}`} trend={12.5} />
            <StatCard label="Individuals" value={data.clients.byType[0].value} />
            <StatCard label="Entities" value={data.clients.byType.slice(1).reduce((a, b) => a + b.value, 0)} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Client Growth */}
            <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
              <h3 className="font-semibold text-white mb-4">Client Growth</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={data.clients.growth}>
                  <defs>
                    <linearGradient id="colorClients" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={COLORS.success} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="cumulative" stroke={COLORS.success} fillOpacity={1} fill="url(#colorClients)" name="Total Clients" />
                  <Bar dataKey="clients" fill={COLORS.primary} name="New Clients" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Top Clients */}
            <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
              <h3 className="font-semibold text-white mb-4">Top Clients</h3>
              <div className="space-y-4">
                {data.clients.topClients.map((client, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <span className="w-8 h-8 rounded-full bg-accent-500/20 flex items-center justify-center text-sm font-medium text-accent-400">
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-white font-medium">{client.name}</p>
                      <div className="flex items-center gap-4 text-sm text-primary-400">
                        <span>{client.documentCount} docs</span>
                        <span>${client.revenue.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="w-24 h-2 bg-primary-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent-500 rounded-full"
                        style={{ width: `${(client.documentCount / data.clients.topClients[0].documentCount) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signatures Report */}
      {reportType === 'signatures' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard label="Sent for Signature" value={data.signatures.sent} />
            <StatCard label="Completed" value={data.signatures.completed} trend={5.2} />
            <StatCard label="Pending" value={data.signatures.pending} />
            <StatCard label="Avg. Time to Sign" value={`${data.signatures.avgTimeToSign} days`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Weekly Signatures */}
            <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
              <h3 className="font-semibold text-white mb-4">Weekly Signature Activity</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.signatures.byWeek}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="week" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="sent" fill={COLORS.secondary} name="Sent" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="completed" fill={COLORS.success} name="Completed" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Completion Gauge */}
            <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
              <h3 className="font-semibold text-white mb-4">Signature Metrics</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="relative w-32 h-32 mx-auto">
                    <svg className="w-32 h-32 -rotate-90">
                      <circle cx="64" cy="64" r="56" fill="none" stroke="#374151" strokeWidth="12" />
                      <circle
                        cx="64" cy="64" r="56" fill="none"
                        stroke={COLORS.success}
                        strokeWidth="12"
                        strokeDasharray={`${(data.signatures.completionRate / 100) * 352} 352`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">{data.signatures.completionRate}%</span>
                    </div>
                  </div>
                  <p className="text-sm text-primary-400 mt-2">Completion Rate</p>
                </div>
                <div className="space-y-4">
                  <div className="p-3 bg-primary-900/50 rounded-lg">
                    <p className="text-sm text-primary-400">Completed</p>
                    <p className="text-xl font-bold text-success-400">{data.signatures.completed}</p>
                  </div>
                  <div className="p-3 bg-primary-900/50 rounded-lg">
                    <p className="text-sm text-primary-400">Pending</p>
                    <p className="text-xl font-bold text-warning-400">{data.signatures.pending}</p>
                  </div>
                  <div className="p-3 bg-primary-900/50 rounded-lg">
                    <p className="text-sm text-primary-400">Declined</p>
                    <p className="text-xl font-bold text-error-400">{data.signatures.declined}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Productivity Report */}
      {reportType === 'productivity' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard label="Efficiency Score" value={`${data.productivity.efficiency}%`} trend={3.2} />
            <StatCard label="Goals Achieved" value={`${data.productivity.goalsHit}/${data.productivity.totalGoals}`} />
            <StatCard label="Avg. Docs/Day" value={Math.round(data.productivity.documentsPerDay.reduce((a, b) => a + b.count, 0) / 5)} />
            <StatCard label="Total Hours" value={data.productivity.timeByTask.reduce((a, b) => a + b.hours, 0)} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Production */}
            <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
              <h3 className="font-semibold text-white mb-4">Documents Per Day</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.productivity.documentsPerDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill={COLORS.primary} radius={[4, 4, 0, 0]} name="Documents" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Time Distribution */}
            <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
              <h3 className="font-semibold text-white mb-4">Time Distribution</h3>
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={250}>
                  <PieChart>
                    <Pie
                      data={data.productivity.timeByTask}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="hours"
                    >
                      {data.productivity.timeByTask.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-3">
                  {data.productivity.timeByTask.map((task, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: task.color }} />
                      <span className="text-sm text-primary-300 flex-1">{task.task}</span>
                      <span className="text-sm text-white font-medium">{task.hours}h</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Metric Card with trend
function MetricCard({
  label,
  value,
  change,
  icon,
  color,
}: {
  label: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'secondary' | 'purple';
}) {
  const colorClasses = {
    primary: 'bg-amber-500/20 text-amber-400',
    success: 'bg-green-500/20 text-green-400',
    secondary: 'bg-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/20 text-purple-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-primary-800/50 rounded-xl border border-primary-700 p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-primary-400">{label}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
          {change !== undefined && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {Math.abs(change)}% vs last period
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </motion.div>
  );
}

// Simple stat card
function StatCard({ label, value, trend }: { label: string; value: string | number; trend?: number }) {
  return (
    <div className="bg-primary-800/50 rounded-xl border border-primary-700 p-6">
      <p className="text-sm text-primary-400">{label}</p>
      <div className="flex items-end gap-2 mt-1">
        <p className="text-3xl font-bold text-white">{value}</p>
        {trend !== undefined && (
          <span className={`text-sm flex items-center gap-0.5 ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  );
}
